import React from 'react'
import queryString from 'query-string'
import { TheNightlySection } from '@news-mono/common'
import { IconChevronUpTN, metrics } from '@news-mono/component-library'
import {
    AdPathsConfig,
    AdTargeting,
    getPaginatedCanonicalUrl,
    parsePageParam,
    RouteInformation,
    RouteResolution,
    RouteServices,
    StaticRouteResolution,
} from '@news-mono/web-common'
import { layout } from '../../App.routing'
import { ResolveSlugTravelEditionV4DTO } from '@west-australian-newspapers/publication-types'

const MAIN_CONTENT_ID = 'main-travel-content'

export function createAllEditionsPage(
    services: RouteServices<RouteResolution>,
    section: TheNightlySection,
    pageTitle: string,
): RouteInformation<TheNightlySection> {
    const baseUrl = services.config.publicUrl + '/editions'
    const query = queryString.parse(services.location.search)
    const page = parsePageParam(query.page)
    const title = `ROAM | The Nightly's Travel Edition${
        page > 1 ? ` | Page ${page}` : ''
    }`
    const canonicalUrl =
        page > 1 ? getPaginatedCanonicalUrl(baseUrl, page) : baseUrl
    const pageDescription =
        'ROAM is a digital-first, premium travel magazine aimed at the discerning traveller. From undiscovered global destinations to the finest new hotels, resorts and adventures that feed the soul. ROAM is your ultimate companion in moving across the world with style and knowledge.'
    const adTargeting = getTravelEditionPageAdTargeting(services.config)

    const socialImage =
        (
            services.resolution as StaticRouteResolution<ResolveSlugTravelEditionV4DTO>
        ).resolution?.meta.socialImage || ''

    return {
        kind: 'page',
        heading: 'Travel Edition',
        pageType: 'static',
        hideHeading: true,
        refreshOnInactive: false,
        pageMeta: {
            title,
            description: pageDescription,
            link: [{ rel: 'canonical', href: canonicalUrl }],
            meta: [
                {
                    property: 'og:image',
                    content: socialImage,
                },
                { name: 'twitter:image', content: socialImage },
                {
                    property: 'og:image:height',
                    content: '630',
                },
                {
                    property: 'og:image:width',
                    content: '1200',
                },
            ],
        },
        socialMeta: {
            title: `${pageTitle} Digital Edition`,
            description: pageDescription,
        },
        adTargeting,
        section: section,
        compositions: [
            layout.composition({
                type: 'thor',
                props: {
                    containerWidth: '100%',
                    horizontalGutters: false,
                    verticalGutters: false,
                },
                contentAreas: {
                    main: [
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                horizontalSpacingBreakpoint: 'sm',
                                horizontalSpacing: 'outerMargin',
                            },
                            contentAreas: {
                                main: [
                                    // Latest Edition
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {
                                            verticalSpacing: 'md',
                                            containerWidth:
                                                metrics.thenightly.siteMetrics
                                                    .mainContentWidth,
                                        },
                                        contentAreas: {
                                            main: [
                                                layout.component({
                                                    type: 'nightly-latest-edition',
                                                    props: {
                                                        dataDefinitionArgs: {
                                                            type: 'edition',
                                                            page: 1,
                                                            pageSize: 1,
                                                            publication:
                                                                'the_nightly_travel',
                                                        },
                                                        title: pageTitle,
                                                        type: 'editions',
                                                        lazyloadImages: true,
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                    // Ad Unit
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {
                                            flex: {
                                                default: {
                                                    hasBeforeAfterMargins: true,
                                                    flexDirection: 'column',
                                                    gap: '40px',
                                                },
                                                lg: {
                                                    hasBeforeAfterMargins: true,
                                                    flexDirection: 'column',
                                                    gap: '80px',
                                                },
                                            },
                                        },
                                        contentAreas: {
                                            main: [
                                                layout.component({
                                                    type: 'ad-unit',
                                                    props: {
                                                        adType: 'inline',
                                                        noticePosition:
                                                            'below-center',
                                                        slot: {
                                                            id: 'editions-page-1',
                                                            size: 'billboardAbove1280leaderboardAbove768MrecBelow',
                                                        },
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                ],
                            },
                        }),
                        // paginated previous editions
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                flex: {
                                    default: {
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        gap: '40px',
                                    },
                                    lg: {
                                        gap: '80px',
                                    },
                                },
                                containerWidth: '100%',
                            },
                            contentAreas: {
                                main: [
                                    layout.nestedComposition({
                                        type: 'collection-data-context',
                                        props: {
                                            contextId: MAIN_CONTENT_ID,
                                            dataDefinitionArgs: {
                                                type: 'edition',
                                                page,
                                                throw404OnNoResults: true,
                                                pageSize: 20,
                                                publication:
                                                    'the_nightly_travel',
                                            },
                                        },
                                        contentAreas: {
                                            children: [
                                                layout.component({
                                                    type: 'nightly-previous-editions',
                                                    props: {
                                                        dataDefinitionArgs: {
                                                            type: 'context',
                                                            contextId:
                                                                MAIN_CONTENT_ID,
                                                            section: 'editions',
                                                        },
                                                        title: 'All Roam Editions',
                                                        type: 'travel-editions',
                                                        lazyloadImages: true,
                                                    },
                                                }),
                                                layout.nestedComposition({
                                                    type: 'box',
                                                    props: {
                                                        horizontalSpacingBreakpoint:
                                                            'sm',
                                                        horizontalSpacing:
                                                            'outerMargin',
                                                        flex: {
                                                            default: {
                                                                flexDirection:
                                                                    'column',
                                                                gap: '40px',
                                                            },
                                                            lg: {
                                                                gap: '32px',
                                                            },
                                                        },
                                                    },
                                                    contentAreas: {
                                                        main: [
                                                            layout.component({
                                                                type: 'pagination',
                                                                props: {
                                                                    contextId:
                                                                        MAIN_CONTENT_ID,
                                                                },
                                                            }),
                                                            layout.component({
                                                                type: 'nightly-button',
                                                                props: {
                                                                    variant:
                                                                        'default',
                                                                    text: 'BACK TO TOP',
                                                                    color: 'primary',
                                                                    fill: 'text',
                                                                    layout: {
                                                                        alignSelf:
                                                                            'center',
                                                                    },
                                                                    action: {
                                                                        type: 'button',
                                                                        onClick:
                                                                            () =>
                                                                                window.scrollTo(
                                                                                    {
                                                                                        top: 0,
                                                                                    },
                                                                                ),
                                                                    },
                                                                    icon: {
                                                                        iconPosition:
                                                                            'right',
                                                                        IconElement:
                                                                            (
                                                                                <IconChevronUpTN />
                                                                            ),
                                                                    },
                                                                },
                                                            }),
                                                        ],
                                                    },
                                                }),
                                                layout.component({
                                                    type: 'ad-unit',
                                                    props: {
                                                        adType: 'inline',
                                                        noticePosition:
                                                            'below-center',
                                                        slot: {
                                                            id: 'editions-page-2',
                                                            size: 'billboardAbove1280leaderboardAbove768MrecBelow',
                                                        },
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                ],
                            },
                        }),
                    ],
                },
            }),
        ],
    }
}

/**
 * Current Travel Edition page targets the ads to a static route, which points to the homepage.
 * We want to override it to map the ad targeting on Travel topic page.
 */
function getTravelEditionPageAdTargeting(config: AdPathsConfig): AdTargeting {
    const topics: string[] = ['Lifestyle', 'Travel']
    const travelEditionPageAdPath: string[] = ['thenightly', ...topics, 'home']

    const adUnitPath: string[] = [
        config.baseAdUnitPath,
        ...travelEditionPageAdPath,
    ]

    return {
        pageId: 'Travel',
        adUnitPath: adUnitPath.join('/'),
        ssAdUnits: [...config.baseAdUnits, ...travelEditionPageAdPath],
        topics: [...topics].reverse(),
    }
}
